import type { SupportedLocale } from '@erp/shared';

export const mcpTranslations = {
  en: {
    readDescription:
      'Read customers, suppliers, products, documents, sales, purchasing, stock, batches and the employee directory.',
    writeDescription: 'Allow supported creation and changes within your existing ERP permissions.',
    title: 'ChatGPT connection',
    description: 'Connect ChatGPT to your ERP and manage your own approvals.',
    help: 'Help with ChatGPT connections',
    setup: 'Connect in ChatGPT',
    setupSteps:
      'In ChatGPT, open Plugins → Add custom MCP server. Paste this URL, select OAuth and sign in to the ERP. Complete any required two-factor verification, then review and approve access here.',
    url: 'MCP server URL',
    copy: 'Copy URL',
    copied: 'URL copied.',
    copyError: 'Could not copy the URL. Select and copy it manually.',
    permissions: 'Access permissions',
    read: 'Read ERP data',
    write: 'Create and change ERP data',
    boundary:
      'Access stays within your current company and ERP permissions. Employee passwords, MFA secrets, pay and private personnel information are excluded.',
    connections: 'Active connections',
    empty: 'No active connections.',
    openChatgpt: 'Set up in ChatGPT',
    created: 'Connected',
    expires: 'Expires',
    revoke: 'Revoke connection',
    revokeTitle: 'Revoke {{name}}?',
    revokeDescription:
      'This immediately ends access for this connection. You can reconnect and approve it again later.',
    revoked: 'Connection revoked.',
    error: 'Connections could not be loaded.',
    retry: 'Try again',
    loading: 'Loading connections',
    consent: 'Approve ChatGPT connection',
    consentDescription:
      '{{name}} requests access to your current company. Review the requested permissions before deciding.',
    allowWrite: 'Allow changes',
    approve: 'Approve connection',
    deny: 'Deny',
    submitting: 'Submitting…',
    decisionError: 'Your decision could not be completed. Please try again.',
    invalidRequest:
      'This approval request is missing, invalid or expired. Start the connection again in ChatGPT.',
    invalidRedirect:
      'The return address could not be verified. Start the connection again in ChatGPT.',
  },
  de: {
    readDescription:
      'Kunden, Lieferanten, Artikel, Belege, Verkauf, Einkauf, Bestände, Chargen und Mitarbeiterverzeichnis lesen.',
    writeDescription:
      'Unterstützte Anlage und Änderungen im Rahmen Ihrer bestehenden ERP-Rechte erlauben.',
    title: 'ChatGPT-Verbindung',
    description: 'ChatGPT mit Ihrem ERP verbinden und eigene Freigaben verwalten.',
    help: 'Hilfe zu ChatGPT-Verbindungen',
    setup: 'In ChatGPT verbinden',
    setupSteps:
      'Öffnen Sie in ChatGPT Plugins → Add custom MCP server. Fügen Sie diese URL ein, wählen Sie OAuth und melden Sie sich im ERP an. Bestätigen Sie bei Bedarf den zweiten Faktor und prüfen und genehmigen Sie anschließend hier den Zugriff.',
    url: 'MCP-Server-URL',
    copy: 'URL kopieren',
    copied: 'URL kopiert.',
    copyError: 'Die URL konnte nicht kopiert werden. Markieren und kopieren Sie sie manuell.',
    permissions: 'Zugriffsrechte',
    read: 'ERP-Daten lesen',
    write: 'ERP-Daten anlegen und ändern',
    boundary:
      'Der Zugriff bleibt auf Ihren aktuellen Mandanten und Ihre ERP-Rechte begrenzt. Mitarbeiterpasswörter, MFA-Geheimnisse, Vergütung und private Personaldaten sind ausgeschlossen.',
    connections: 'Aktive Verbindungen',
    empty: 'Keine aktiven Verbindungen.',
    openChatgpt: 'In ChatGPT einrichten',
    created: 'Verbunden am',
    expires: 'Gültig bis',
    revoke: 'Verbindung widerrufen',
    revokeTitle: '{{name}} widerrufen?',
    revokeDescription:
      'Der Zugriff dieser Verbindung endet sofort. Sie können sie später neu verbinden und freigeben.',
    revoked: 'Verbindung widerrufen.',
    error: 'Verbindungen konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    loading: 'Verbindungen werden geladen',
    consent: 'ChatGPT-Verbindung freigeben',
    consentDescription:
      '{{name}} bittet um Zugriff auf Ihren aktuellen Mandanten. Prüfen Sie die angefragten Rechte vor Ihrer Entscheidung.',
    allowWrite: 'Änderungen erlauben',
    approve: 'Verbindung freigeben',
    deny: 'Ablehnen',
    submitting: 'Wird übermittelt …',
    decisionError: 'Ihre Entscheidung konnte nicht abgeschlossen werden. Bitte erneut versuchen.',
    invalidRequest:
      'Diese Freigabeanfrage fehlt, ist ungültig oder abgelaufen. Starten Sie die Verbindung erneut in ChatGPT.',
    invalidRedirect:
      'Die Rücksprungadresse konnte nicht geprüft werden. Starten Sie die Verbindung erneut in ChatGPT.',
  },
  fr: {
    readDescription:
      'Lire les clients, fournisseurs, produits, documents, ventes, achats, stocks, lots et l’annuaire du personnel.',
    writeDescription:
      'Autoriser les créations et modifications prises en charge selon vos droits ERP actuels.',
    title: 'Connexion ChatGPT',
    description: 'Connectez ChatGPT à votre ERP et gérez vos propres autorisations.',
    help: 'Aide sur les connexions ChatGPT',
    setup: 'Connecter dans ChatGPT',
    setupSteps:
      'Dans ChatGPT, ouvrez Plugins → Add custom MCP server. Collez cette URL, choisissez OAuth et connectez-vous à l’ERP. Effectuez la vérification à deux facteurs si nécessaire, puis examinez et autorisez ici l’accès.',
    url: 'URL du serveur MCP',
    copy: 'Copier l’URL',
    copied: 'URL copiée.',
    copyError: 'Impossible de copier l’URL. Sélectionnez-la et copiez-la manuellement.',
    permissions: 'Droits d’accès',
    read: 'Lire les données ERP',
    write: 'Créer et modifier les données ERP',
    boundary:
      'L’accès reste limité à votre entreprise actuelle et à vos droits ERP. Les mots de passe, secrets MFA, rémunérations et données privées du personnel sont exclus.',
    connections: 'Connexions actives',
    empty: 'Aucune connexion active.',
    openChatgpt: 'Configurer dans ChatGPT',
    created: 'Connexion créée',
    expires: 'Expiration',
    revoke: 'Révoquer la connexion',
    revokeTitle: 'Révoquer {{name}} ?',
    revokeDescription:
      'L’accès de cette connexion prend fin immédiatement. Vous pourrez la reconnecter et l’autoriser plus tard.',
    revoked: 'Connexion révoquée.',
    error: 'Impossible de charger les connexions.',
    retry: 'Réessayer',
    loading: 'Chargement des connexions',
    consent: 'Autoriser la connexion ChatGPT',
    consentDescription:
      '{{name}} demande l’accès à votre entreprise actuelle. Examinez les droits demandés avant de décider.',
    allowWrite: 'Autoriser les modifications',
    approve: 'Autoriser la connexion',
    deny: 'Refuser',
    submitting: 'Envoi…',
    decisionError: 'Votre décision n’a pas pu aboutir. Veuillez réessayer.',
    invalidRequest:
      'Cette demande d’autorisation est absente, invalide ou expirée. Relancez la connexion dans ChatGPT.',
    invalidRedirect:
      'L’adresse de retour n’a pas pu être vérifiée. Relancez la connexion dans ChatGPT.',
  },
  es: {
    readDescription:
      'Leer clientes, proveedores, productos, documentos, ventas, compras, existencias, lotes y el directorio de empleados.',
    writeDescription:
      'Permitir las creaciones y los cambios admitidos dentro de sus permisos ERP actuales.',
    title: 'Conexión con ChatGPT',
    description: 'Conecte ChatGPT a su ERP y gestione sus propias autorizaciones.',
    help: 'Ayuda sobre conexiones con ChatGPT',
    setup: 'Conectar en ChatGPT',
    setupSteps:
      'En ChatGPT, abra Plugins → Add custom MCP server. Pegue esta URL, seleccione OAuth e inicie sesión en el ERP. Complete la verificación de dos factores si se requiere y revise y autorice aquí el acceso.',
    url: 'URL del servidor MCP',
    copy: 'Copiar URL',
    copied: 'URL copiada.',
    copyError: 'No se pudo copiar la URL. Selecciónela y cópiela manualmente.',
    permissions: 'Permisos de acceso',
    read: 'Leer datos del ERP',
    write: 'Crear y cambiar datos del ERP',
    boundary:
      'El acceso se limita a su empresa actual y sus permisos del ERP. Se excluyen contraseñas, secretos MFA, remuneraciones y datos privados del personal.',
    connections: 'Conexiones activas',
    empty: 'No hay conexiones activas.',
    openChatgpt: 'Configurar en ChatGPT',
    created: 'Conectado',
    expires: 'Caduca',
    revoke: 'Revocar conexión',
    revokeTitle: '¿Revocar {{name}}?',
    revokeDescription:
      'El acceso de esta conexión finaliza de inmediato. Puede volver a conectarla y autorizarla más adelante.',
    revoked: 'Conexión revocada.',
    error: 'No se pudieron cargar las conexiones.',
    retry: 'Reintentar',
    loading: 'Cargando conexiones',
    consent: 'Autorizar conexión con ChatGPT',
    consentDescription:
      '{{name}} solicita acceso a su empresa actual. Revise los permisos solicitados antes de decidir.',
    allowWrite: 'Permitir cambios',
    approve: 'Autorizar conexión',
    deny: 'Denegar',
    submitting: 'Enviando…',
    decisionError: 'No se pudo completar su decisión. Inténtelo de nuevo.',
    invalidRequest:
      'Esta solicitud falta, no es válida o ha caducado. Reinicie la conexión en ChatGPT.',
    invalidRedirect:
      'No se pudo verificar la dirección de retorno. Reinicie la conexión en ChatGPT.',
  },
  tr: {
    readDescription:
      'Müşterileri, tedarikçileri, ürünleri, belgeleri, satışları, satın almayı, stokları, partileri ve çalışan dizinini okuyun.',
    writeDescription:
      'Mevcut ERP izinleriniz kapsamında desteklenen oluşturma ve değişikliklere izin verin.',
    title: 'ChatGPT bağlantısı',
    description: 'ChatGPT’yi ERP’nize bağlayın ve kendi onaylarınızı yönetin.',
    help: 'ChatGPT bağlantısı yardımı',
    setup: 'ChatGPT’de bağlanın',
    setupSteps:
      'ChatGPT’de Plugins → Add custom MCP server yolunu açın. Bu URL’yi yapıştırın, OAuth seçin ve ERP’ye giriş yapın. Gerekiyorsa iki faktörlü doğrulamayı tamamlayın, ardından erişimi burada inceleyip onaylayın.',
    url: 'MCP sunucusu URL’si',
    copy: 'URL’yi kopyala',
    copied: 'URL kopyalandı.',
    copyError: 'URL kopyalanamadı. Seçip elle kopyalayın.',
    permissions: 'Erişim izinleri',
    read: 'ERP verilerini oku',
    write: 'ERP verileri oluştur ve değiştir',
    boundary:
      'Erişim mevcut şirketiniz ve ERP izinlerinizle sınırlıdır. Çalışan parolaları, MFA sırları, ücretler ve özel personel bilgileri kapsam dışıdır.',
    connections: 'Etkin bağlantılar',
    empty: 'Etkin bağlantı yok.',
    openChatgpt: 'ChatGPT’de kur',
    created: 'Bağlanma tarihi',
    expires: 'Son geçerlilik',
    revoke: 'Bağlantıyı iptal et',
    revokeTitle: '{{name}} iptal edilsin mi?',
    revokeDescription:
      'Bu bağlantının erişimi hemen sona erer. Daha sonra yeniden bağlanıp onaylayabilirsiniz.',
    revoked: 'Bağlantı iptal edildi.',
    error: 'Bağlantılar yüklenemedi.',
    retry: 'Yeniden dene',
    loading: 'Bağlantılar yükleniyor',
    consent: 'ChatGPT bağlantısını onayla',
    consentDescription:
      '{{name}} mevcut şirketinize erişim istiyor. Karar vermeden önce istenen izinleri inceleyin.',
    allowWrite: 'Değişikliklere izin ver',
    approve: 'Bağlantıyı onayla',
    deny: 'Reddet',
    submitting: 'Gönderiliyor…',
    decisionError: 'Kararınız tamamlanamadı. Lütfen yeniden deneyin.',
    invalidRequest:
      'Bu onay isteği eksik, geçersiz veya süresi dolmuş. Bağlantıyı ChatGPT’de yeniden başlatın.',
    invalidRedirect: 'Dönüş adresi doğrulanamadı. Bağlantıyı ChatGPT’de yeniden başlatın.',
  },
} as const satisfies Record<SupportedLocale, Record<string, string>>;
